import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import usePageTitle from '../../../hooks/usePageTitle';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbContext';
import {
  fetchUpcomingSchedule,
  selectUpcomingSchedule,
  selectUpcomingScheduleLoading,
} from '../../../slices/marketSlice';
import {
  selectSelectedResource,
  setSelectedResource,
} from '../../../slices/preferencesSlice';
import {
  fetchResources,
  selectResources,
  selectResourcesLoaded,
} from '../../../slices/userSlice';
import RhythmPanel from './RhythmPanel';
import NextOpeningsPanel from './NextOpeningsPanel';
import MobileScheduleAgenda from './MobileScheduleAgenda';

const WINDOW_OPTIONS = [
  { label: '24h', value: 24 },
  { label: '48h', value: 48 },
  { label: '7d', value: 168 },
];

const SCHEDULE_REFRESH_MS = 60 * 1000;
const CLOCK_TICK_MS = 30 * 1000;

export default function SchedulePage() {
  usePageTitle('Schedule');
  useBreadcrumbs([{ label: 'Schedule' }], 'Upcoming challenge openings');

  const dispatch = useDispatch();
  const schedule = useSelector(selectUpcomingSchedule);
  const loading = useSelector(selectUpcomingScheduleLoading);
  const selectedResource = useSelector(selectSelectedResource);
  const resources = useSelector(selectResources);
  const resourcesLoaded = useSelector(selectResourcesLoaded);

  const [windowHours, setWindowHours] = useState(48);
  const [now, setNow] = useState(() => Date.now());

  const resourceId = selectedResource?.id || null;

  // Ensure the resource list (for the selector) is loaded.
  useEffect(() => {
    if (!resourcesLoaded) dispatch(fetchResources());
  }, [resourcesLoaded, dispatch]);

  // Fetch the selected resource's schedule; keep it fresh on an interval.
  useEffect(() => {
    if (!resourceId) return undefined;
    const load = () =>
      dispatch(fetchUpcomingSchedule({ windowHours, resourceId }));
    load();
    const id = setInterval(load, SCHEDULE_REFRESH_MS);
    return () => clearInterval(id);
  }, [dispatch, windowHours, resourceId]);

  // Tick the countdown clock independently of data fetches.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const sortedResources = useMemo(() => {
    const list = [...(resources || [])];
    list.sort((a, b) => {
      const activeDelta = (a.is_active === false) - (b.is_active === false);
      if (activeDelta !== 0) return activeDelta;
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [resources]);

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header className="space-y-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Upcoming schedule
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            When forecasting challenges will open for the selected resource, so
            you can plan your submissions ahead.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={resourceId || ''}
            onChange={(event) => dispatch(setSelectedResource(event.target.value))}
            className="h-8 max-w-[16rem] rounded-md border border-gray-200/70 bg-surface px-2 text-xs font-medium text-gray-700 dark:border-gray-600/70 dark:bg-gray-870 dark:text-gray-200"
          >
            {!resourceId && <option value="">Select a resource…</option>}
            {sortedResources.map((resource) => (
              <option key={resource.id} value={resource.id}>
                {resource.name}
                {resource.is_active === false ? ' (inactive)' : ''}
              </option>
            ))}
          </select>
          <div className="inline-flex rounded-md border border-gray-200/70 p-0.5 dark:border-gray-600/70">
            {WINDOW_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setWindowHours(option.value)}
                className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                  windowHours === option.value
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {!resourceId ? (
        <div className="rounded-lg border border-gray-200/70 bg-surface p-6 text-sm text-gray-500 dark:border-gray-600/70 dark:bg-gray-870 dark:text-gray-400">
          Select a resource to see its upcoming schedule.
        </div>
      ) : (
        <>
          {/* Phones: one chronological agenda instead of the two panels */}
          <div className="lg:hidden">
            <MobileScheduleAgenda openings={schedule.openings} loading={loading} now={now} />
          </div>
          <div className="hidden lg:grid grid-cols-1 gap-6 lg:grid-cols-2">
            <RhythmPanel patterns={schedule.patterns} loading={loading} />
            <NextOpeningsPanel openings={schedule.openings} loading={loading} now={now} />
          </div>
        </>
      )}

      <p className="text-[11px] text-gray-400 dark:text-gray-500">
        This schedule is a projection and may change, so treat it as a guide,
        not a guarantee.
      </p>
    </div>
  );
}
